import http from "../../helpers/http";
import * as helpers from "../../helpers/helpers";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../utils/siteSettings";

import {
  FETCH_CHECKOUT_CONTENT,
  FETCH_CHECKOUT_CONTENT_SUCCESS,
  FETCH_CHECKOUT_CONTENT_FAILED,
  SAVE_SUBSCRIPTION_PAYMENT,
  SAVE_SUBSCRIPTION_PAYMENT_SUCCESS,
  SAVE_SUBSCRIPTION_PAYMENT_FAILED
} from "./actionTypes";

export const fetchCheckout = (formData) => (dispatch) => {
  dispatch({
    type: FETCH_CHECKOUT_CONTENT,
    payload: null
  });

  // Enterprise-user-checkout auto-login: the API returns a fresh authToken
  // in the response so a not-yet-logged-in user from an email invite can
  // proceed. Without isolating this call, two failure modes happen:
  //   (1) A stale/expired token in localStorage races the App.jsx mount-
  //       time fetchSiteSettings, which returns not_logged and trips the
  //       localStorage.clear() + redirect-to-/login branch before our
  //       fresh token can land.
  //   (2) MORE COMMON: a DIFFERENT VALID user's token is in localStorage
  //       (e.g. someone else was logged in on this browser). The backend
  //       resolves checkout-page to that other member, returns a
  //       non-matching plan, and the status:0 branch bounces to /pricing.
  // We need to do TWO things so neither variant fires:
  //   - Remove the token from localStorage so the mount-time
  //     fetchSiteSettings call sees no auth and skips its redirect path.
  //   - Override the Authorization header on THIS request to empty,
  //     because http.js captures localStorage.getItem("authToken") at
  //     module-load time and bakes it into the axios instance — stripping
  //     localStorage alone does NOT remove the header from in-flight or
  //     subsequent requests using the cached instance. With the header
  //     blanked, the backend has no session to resolve from and MUST use
  //     user_encoded_id to identify the invited member.
  const isEnterpriseUserCheckout =
    (formData?.enterprise === 1 || formData?.enterprise === "1") &&
    !!formData?.user_encoded_id;
  if (isEnterpriseUserCheckout) {
    localStorage.removeItem("authToken");
  }

  const requestConfig = isEnterpriseUserCheckout
    ? { headers: { Authorization: "" } }
    : undefined;

  http
    .post("checkout-page", helpers.doObjToFormData(formData), requestConfig)
    .then(({ data }) => {
      if (data?.status === 0 && data?.no_member === 1) {
        window.location.href = "/login";
        return;
      }
      if (data?.status === 0) {
        window.location.href = "/pricing";
        return;
      }

      // Persist auto-login token from a successful enterprise-user-checkout
      // response. The http interceptor reads localStorage on every request,
      // so subsequent API calls (make-subscription-payment, etc.) will pick
      // it up automatically — no need to force-dispatch fetchSiteSettings
      // here.
      //
      // Earlier versions of this code dispatched either
      // SIGN_IN_ACCOUNT_POPUP_MESSAGE_SUCCESS or fetchSiteSettings to
      // refresh the member. Both caused trouble: the SIGN_IN dispatch fed
      // a runaway-loop because App.jsx defines its layout components as
      // closures inside App, so every state change re-mints those
      // functions, which React Router treats as new types → remount →
      // re-fire fetchCheckout → new token → state change → loop (272
      // calls/min). The fetchSiteSettings dispatch instead reset
      // site_settings to null mid-load, leaving Checkout stuck on a
      // spinner if the follow-up call hung or tripped the not_logged
      // redirect. The Header will refresh its member view on the next
      // page navigation; that's an acceptable tradeoff vs. blank pages.
      if (data?.authToken) {
        localStorage.setItem("authToken", data.authToken);
        if (data?.mem_email) {
          localStorage.setItem("email", data.mem_email);
        }
      }

      dispatch({
        type: FETCH_CHECKOUT_CONTENT_SUCCESS,
        payload: data
      });
    })
    .catch((error) => {
      console.error("Error fetching checkout content:", error);
      dispatch({
        type: FETCH_CHECKOUT_CONTENT_FAILED,
        payload: error
      });
    });
};

export const makeSubsPayment = (formData) => (dispatch) => {
  if (localStorage.getItem("authToken")) {
    dispatch({
      type: SAVE_SUBSCRIPTION_PAYMENT,
      payload: null
    });
    formData = { ...formData, token: localStorage.getItem("authToken") };
    return http
      .post("make-subscription-payment", helpers.doObjToFormData(formData))
      .then(({ data }) => {
        
        if (data.status) {
          toast.success(
            "You have successfully subscribed the plan.",
            TOAST_SETTINGS
          );
          dispatch({
            type: SAVE_SUBSCRIPTION_PAYMENT_SUCCESS,
            payload: data
          });
          setTimeout(() => {
            window.location.replace("/dashboard");
          }, 2000);
          return data;
        } else {
          if (data?.msg) {
            toast.error(data.msg, TOAST_SETTINGS);
          }
          dispatch({
            type: SAVE_SUBSCRIPTION_PAYMENT_FAILED,
            payload: null
          });
          return data;
        }
      })
      .catch((error) => {
        dispatch({
          type: SAVE_SUBSCRIPTION_PAYMENT_FAILED,
          payload: error
        });
        return {
          status: 0,
          msg: error?.response?.data?.msg || "Unable to save subscription."
        };
        // const { data } = error.response;
        // window.location.reload();
      });
  } else {
    toast.error("Please signin first to save this job post.", TOAST_SETTINGS);
    return Promise.resolve({ status: 0, msg: "Please sign in first." });
  }
};
